import {AfterViewInit, Component, OnDestroy, OnInit, ViewEncapsulation} from '@angular/core';
import {IDialogView} from '@shared/components/ok-cancel-dialog/ok-cancel-dialog.component';
import {DialogModel} from '@shared/models/dialog.model';
import {Observable, Subject} from 'rxjs';
import {TranslationSupportiveLanguage} from '../../../models/translation.model';
import {SnackBarService} from '@core/services/snack-bar.service';
import {FuseSplashScreenService} from '@barebone-fuse-services/splash-screen.service';
import {MatDialogRef} from '@angular/material/dialog';
import {select, Store} from '@ngrx/store';
import {takeUntil} from 'rxjs/operators';
import {getQueryResponse} from '@shared/store/selectors/query.selector';
import {QueryProvider} from '@shared/queries/query-provider';
import {StringUtility} from '@shared/utility/string-utility';
import {IQueryState} from '@shared/store/reducers/query.reducer';
import {TranslationService} from '../../../services/translation.service';
import {FileManagementService} from '@core/services/file-management.service';
import {StorageSettings, TranslationToolNames} from '../../../models/translation.constants';
import {hasFeature} from '@shared/store/selectors/security.selector';
import {SecurityState} from '@shared/store/reducers/security.reducer';

@Component({
    standalone: false,
    selector: 'particle-translation-setting',
  templateUrl: './translation-setting.component.html',
  styleUrls: ['./translation-setting.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class TranslationSettingComponent implements OnInit, IDialogView, OnDestroy, AfterViewInit {
  data: DialogModel;
  supportiveLanguageQueryKey = 'TranslationSupportiveLanguage';
  unsubscribe$ = new Subject<void>();
  fileToUpload: File;
  isFileScanned: boolean;
  toLanguage: string;
  fromLanguage: string;
  translationSupportiveLanguage: TranslationSupportiveLanguage[] = [];
  isAcceptedFileFormat = true;
  pdfSelected = false;
  docOrExcelSelected = false;
  fileExtention: string = null;
  fileIdByUploading: string = null;
  isNotAForm: boolean;
  isDriveForm = false;
  translationId: string;
  storageSettings = null;
  storageOptions = StorageSettings;
  hasFeature$: Observable<any>;
  hasKPMGFeature: boolean = false;

  private language$: any;

  constructor(private snackBarService: SnackBarService,
              private securityStore: Store<SecurityState>,
              private fuseSplashScreenService: FuseSplashScreenService,
              private fileManagementService: FileManagementService,
              private translationService: TranslationService,
              public dialogRef: MatDialogRef<TranslationSettingComponent>,
              private queryStore: Store<IQueryState>) {
    this.isFileScanned = false;
    this.fileExtention = null;
    this.fromLanguage = null;
    this.toLanguage = null;
    this.isDriveForm = false;
  }


  ngOnInit() {
    this.isDriveForm = this.data.input.isDriveForm;
    this.translationId = this.data.input.translationId;
    if (this.isDriveForm) {
      this.checkValidFile(this.data.input.fileId, this.data.input.fileName);
    }

    this.hasFeature$ = this.securityStore.pipe(select(hasFeature));

    this.hasFeature$.pipe(takeUntil(this.unsubscribe$)).subscribe(x => {
      this.hasKPMGFeature = x('KPMG');
      if (this.hasKPMGFeature) {
        this.storageSettings = StorageSettings.NonStored;
      } else {
        this.storageSettings = StorageSettings.Stored;
      }
    });


    this.language$ = this.queryStore.pipe(select(getQueryResponse, this.supportiveLanguageQueryKey), takeUntil(this.unsubscribe$));
    this.language$.subscribe(language => {
      if (language) {
        this.translationSupportiveLanguage = language;
        if (this.translationSupportiveLanguage && this.translationSupportiveLanguage.length > 0) {
          this.translationSupportiveLanguage = this.translationService.sortTranslationSupportiveLanguage(this.translationSupportiveLanguage);
        }
      }
    });
    QueryProvider.invokeQuery(this.queryStore, this.supportiveLanguageQueryKey, {});
  }

  onOkClicked(): boolean {
    this.fileExtention = StringUtility.getFileExtension(this.fileToUpload).toLowerCase();
    if (!this.fileToUpload) {
      this.snackBarService.showError('Please select a file.');
      return false;
    }
    if (!this.fromLanguage) {
      this.snackBarService.showError('Please select a from language.');
      return;
    }
    if (!this.toLanguage) {
      this.snackBarService.showError('Please select a to language.');
      return;
    }

    if (this.isFileScanned && (this.translationService.isOcrNotSupportiveLanguageSelected(this.fromLanguage) || this.translationService.isOcrNotSupportiveLanguageSelected(this.toLanguage))) {
      this.snackBarService.showError('The language in the scanned document you are uploading is not supported by our OCR engine.\n' +
        'Please upload an electronic version of the document.');
      return false;
    }

    this.data.input.formConfig = {
      fileType: this.fileExtention,
      isFileScanned: this.isFileScanned,
      fromLanguage: this.fromLanguage,
      toLanguage: this.toLanguage,
      file: this.fileToUpload,
      toolName: TranslationToolNames.Translation,
      pdfSelected: this.pdfSelected,
      docOrExcelSelected: this.docOrExcelSelected,
      fileName: this.fileToUpload.name,
      translationId: this.translationId,
      storageSettings: this.storageSettings
    };
    console.log('formconfig:', this.data.input.formConfig);
    if (!this.isDriveForm && this.fileToUpload) {
      this.fileUploader();
    } else if (this.isDriveForm) {
      this.data.input.formConfig.fileId = this.data.input.fileId;
      return true;
    }
    return false;
  }

  ngAfterViewInit(): void {
  }

  handleCheckbox() {
  }

  handleFileInput() {
    const expectedType: any[] = ['.pdf', '.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx'];
    const fileFormatOutput = this.fileManagementService.handleFileInput(this.fileToUpload, expectedType);
    if (fileFormatOutput) {
      this.isAcceptedFileFormat = true;
      this.checkForPdfOrDoc();
    } else {
      this.snackBarService.showError('Please select a file of supported format. Accepted formats: PDF, DOC, XLS');
      if (this.isDriveForm) {
        this.closeDialog();
      }
      this.isAcceptedFileFormat = false;
      this.fileToUpload = null;
      this.pdfSelected = false;
      this.docOrExcelSelected = false;
    }
  }

  fileUploader() {
    this.fuseSplashScreenService.show();
    this.translationService.uploadClientFile(this.fileToUpload, this.translationId).pipe(takeUntil(this.unsubscribe$)).subscribe(fileId => {
      if (fileId) {
        this.fuseSplashScreenService.hide();
        this.data.input.formConfig.fileId = fileId;
        this.dialogRef.close({
          action: 'OK',
          data: this.data
        });
      }
    });
  }

  ngOnDestroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  private checkForPdfOrDoc() {
    this.fileExtention = StringUtility.getFileExtension(this.fileToUpload.name);
    this.fileExtention = this.fileExtention.toLowerCase();
    if (this.fileExtention) {
      if (this.fileExtention === '.pdf') {
        this.docOrExcelSelected = false;
        this.pdfSelected = true;
      } else if (this.fileExtention === '.xlsx' || this.fileExtention === '.doc' || this.fileExtention === '.docx' || this.fileExtention === '.xls') {
        this.docOrExcelSelected = true;
        this.pdfSelected = false;
      } else {
        this.docOrExcelSelected = false;
        this.pdfSelected = false;
      }
    }
  }

  private checkValidFile(fileId, fileName) {
    if (!fileId) {
      this.snackBarService.showError('File id not found.');
      this.closeDialog();
    }
    if (!fileName) {
      this.snackBarService.showError('File name not found.');
      this.closeDialog();
    }
    this.fileToUpload = new File(['<html>' + '' + '</html>'], fileName);
    this.handleFileInput();
  }

  private closeDialog() {
    this.dialogRef.close({
      action: 'close',
      data: this.data
    });
  }
}





